<template>
    <div class="order-received-popover-content">
        <OrderReceivedHeader
            :isAnyFulfillmentReady="isAnyFulfillmentReady"
            :animateIcon="animateIcon"
            :dailyOrderIncrement="confirmation.dailyOrderIncrement"
            :orderNumber="confirmation.orderNumber"
        />
        <PaymentSummary :confirmation="confirmation" />
        <PreorderPickupTimeSummary :orderPickUpDateTime="orderPickUpDateTime" :useCase="useCase" />
        <LocationSummary :useCase="useCase" :allocation="allocation" />
        <UseCaseSummary
            :useCase="useCase"
            :tableName="confirmation.tableName"
            :isAnyFulfillment="isAnyFulfillment"
            :isAnyFulfillmentReady="isAnyFulfillmentReady"
        />

        <div class="fulfillment-list-container" ref="fulfillmentListContainer"></div>

        <template v-if="isWaiterOrAdmin">
            <template v-if="showReceiptActionsForOperator">
                <!-- For operator with receipt actions show "more"/"receipt and more" and "continue" -->
                <button v-if="confirmation.isPayLater" class="btn" type="button" @click="onMoreClick">
                    {{ trans.orderReceivedPopover.more }}
                </button>

                <button v-if="!confirmation.isPayLater" class="btn" type="button" @click="onMoreClick">
                    {{ trans.orderReceivedPopover.receiptAndMore }}
                </button>

                <button class="btn transparent" type="button" @click="onContinueClick">
                    {{ trans.general.continue }}
                </button>
            </template>

            <template v-if="!showReceiptActionsForOperator">
                <button v-if="confirmation.isPayLater" class="btn" type="button" @click="onMoreClick">
                    {{ trans.orderReceivedPopover.more }}
                </button>

                <!-- For operator with no receipt actions - show only "continue" -->
                <button
                    class="btn"
                    type="button"
                    :class="{ transparent: confirmation.isPayLater }"
                    @click="onContinueClick"
                >
                    {{ trans.general.continue }}
                </button>
            </template>
        </template>

        <template v-if="isEndUser">
            <button v-if="confirmation.isPayLater" class="btn" type="button" @click="onMoreClick">
                {{ trans.general.continue }}
            </button>

            <button v-if="!confirmation.isPayLater" class="btn" type="button" @click="onMoreClick">
                {{ trans.orderReceivedPopover.receiptAndMore }}
            </button>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { RealtimeFulfillmentList } from "@/app/elements/RealtimeFulfillmentList";
    import { trans } from "@/app/localization/Trans";
    import { Helper } from "@/app/ui/Helper";
    import type { IOrderPaymentConfirmation } from "@/data/model/Customer";
    import type { IOrderUseCase } from "@/data/model/OrderUseCase";
    import type { ITableAllocation } from "@/data/model/Table";
    import { logError } from "@/shared/logging/errors";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

    import LocationSummary from "@/app/components/payment/orderReceived/LocationSummary.vue";
    import OrderReceivedHeader from "@/app/components/payment/orderReceived/OrderReceivedHeader.vue";
    import PaymentSummary from "@/app/components/payment/orderReceived/PaymentSummary.vue";
    import PreorderPickupTimeSummary from "@/app/components/payment/orderReceived/PreorderPickupTimeSummary.vue";
    import UseCaseSummary from "@/app/components/payment/orderReceived/UseCaseSummary.vue";

    const props = defineProps<{
        confirmation: IOrderPaymentConfirmation;
        isWaiterOrAdmin: boolean;
        allocation: ITableAllocation | undefined;
        orderUseCase: IOrderUseCase | undefined;
        showReceiptActionsForOperator: boolean;

        onStartOver?: () => void;
        onReceiptAndMore?: () => void;
        hide?: () => void;
        updateBodyHeight?: () => void;
    }>();

    // State
    const orderId = computed(() => props.confirmation.orderId!);
    const orderServiceId = computed(() => props.confirmation.orderServiceId);
    const useCase = ref(props.orderUseCase);
    const orderPickUpDateTime = ref<Date | undefined>(props.confirmation.scheduledFulfillmentDateTime);
    const isEndUser = computed(() => !props.isWaiterOrAdmin);
    const animateIcon = ref(false);

    // fulfillments
    const fulfillmentListContainer = ref<HTMLDivElement | null>(null);
    let fulfillments: RealtimeFulfillmentList;

    const isAnyFulfillment = ref(false);
    const isAnyFulfillmentReady = ref(false);

    function setupFulfillmentList() {
        const $fulfillmentsContainer = $(fulfillmentListContainer.value!);
        fulfillments = new RealtimeFulfillmentList(orderId.value).renderTo($fulfillmentsContainer);
        fulfillments.onListChanged = () => onFulfillmentListChanged();

        onFulfillmentListChanged();
    }

    function onFulfillmentListChanged() {
        isAnyFulfillment.value = fulfillments.hasAnyFulfillment();
        isAnyFulfillmentReady.value = fulfillments.hasAnyFulfillmentReady();

        if (props.updateBodyHeight) {
            props.updateBodyHeight();
        }
    }
    // end fulfillments

    function onMoreClick() {
        if (props.hide) {
            props.hide();
        }
        if (props.onReceiptAndMore) {
            props.onReceiptAndMore();
        }
    }

    function onContinueClick() {
        if (props.onStartOver) {
            props.onStartOver();
        }
        if (props.hide) {
            props.hide();
        }
    }

    // lifecycle
    onMounted(async () => {
        setupFulfillmentList();
        TastierClient.context.onFulfillmentsChanged.add(onFulfillmentsChanged);
        fulfillments.startListening();

        await refreshHeight();

        window.setTimeout(async () => {
            animateIcon.value = true;
            await refreshHeight();
        }, 400);
    });

    onBeforeUnmount(() => {
        TastierClient.context.onFulfillmentsChanged.remove(onFulfillmentsChanged);
        fulfillments?.stopListening();
    });

    function onFulfillmentsChanged() {
        try {
            const newOrderPickUpDateTime = TastierClient.context.getActiveOrderPickUpDateTime(orderId.value);
            orderPickUpDateTime.value = newOrderPickUpDateTime;
        } catch (ex) {
            logError(ex);
        }
    }

    async function forceRefreshUseCaseSection() {
        try {
            useCase.value = Helper.getUseCaseByOrderOrOfferServiceId(orderServiceId.value);
            isAnyFulfillment.value = fulfillments.hasAnyFulfillment();
            isAnyFulfillmentReady.value = fulfillments.hasAnyFulfillmentReady();

            await refreshHeight();
        } catch (ex) {
            logError(ex);
        }
    }

    // todo: obsolete, remove after confirm flex-height is working
    async function refreshHeight() {
        await nextTick();
        if (props.updateBodyHeight) {
            props.updateBodyHeight();
        }
    }

    defineExpose({
        refreshUseCaseSection: forceRefreshUseCaseSection,
    });

    watch(orderPickUpDateTime, refreshHeight);
    watch(isAnyFulfillment, refreshHeight);
    watch(isAnyFulfillmentReady, refreshHeight);
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    // todo: obsolete, remove after confirm flex-height is working
    .popover.order-received-popover {
        > .head {
            height: 46px;
        }

        > .body {
            top: 46px;
        }
    }

    .order-received-popover-content {
        padding-bottom: 1px; // todo: obsolete, remove after confirm flex-height is working

        .fulfillmentlist {
            max-height: 184px;
            min-height: 54px;
            overflow: auto;
            margin: 0 -26px;
            padding: 0 26px 14px 26px;
        }

        > .btn {
            display: block;
            height: 46px;
            margin: 7px 0 18px 0;
            background-color: var(--colorPrimary);
            color: #fff;
            border-radius: 23px;
            width: 100%;
            font-size: 17px;

            &.transparent {
                background-color: transparent;
                color: black;

                @include mixins.hover-and-active() {
                    background-color: transparent;
                }
            }

            @include mixins.hover-and-active() {
                background-color: var(--colorPrimaryHover);
            }
        }
    }
</style>
